import React from "react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { Building2, Check, ChevronDown, FileText, Users } from "lucide-react";

import { Card, CardContent, CardHeader } from "js/components/ui/card";
import { StatusBadge } from "js/components/ui/status-badge";

import {
  GuarantorDetails,
  LandlordDetails,
  TenantDetails,
} from "./typing";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AdditionalEntry {
  subfolder_id: string;
  slug?: string;
  full_name?: string | null;
  person_index?: number | null;
  status?: string | null;
  is_principal?: boolean | null;
  legal_type?: "natural" | "moral" | null;
  email?: string | null;
  phone?: string | null;
  rfc?: string | null;
  curp?: string | null;
  birth_date?: string | null;
  represents_subfolder_id?: string | null;
  represents_name?: string | null;
  legal_rep?: LegalRep | null;
}

interface LegalRep {
  full_name?: string | null;
  email?: string | null;
  phone?: string | null;
  rfc?: string | null;
}

export interface AdditionalParticipants {
  tenants?: AdditionalEntry[];
  guarantors?: AdditionalEntry[];
  landlords?: AdditionalEntry[];
  representatives?: AdditionalEntry[];
}

interface PersonDetail {
  email?: string | null;
  phone?: string | null;
  birthDate?: string | null;
  rfc?: string | null;
  curp?: string | null;
}

interface Person {
  id: string;
  initials: string;
  fullName: string;
  roleLabel: string;
  legalTypeLabel: string;
  isPrincipal: boolean;
  isCompany: boolean;
  isValidated: boolean;
  detail: PersonDetail;
  reps: Person[];
  legalRep: LegalRep | null;
  role: "tenant" | "guarantor" | "landlord" | "rep";
  defaultOpen: boolean;
  // Candidate multiexpediente subfolder slugs = required_documents keys, used to
  // smooth-scroll to this person's document group.
  docSlugs: string[];
}

interface Group {
  key: string;
  title: string;
  people: Person[];
}

interface Props {
  tenant?: TenantDetails | null;
  guarantor?: GuarantorDetails | null;
  landlord?: LandlordDetails | null;
  additionalParticipants?: AdditionalParticipants;
  // Rendered inside the expanded detail of the PRINCIPAL (investigated) guarantor row —
  // e.g. the VIF upsell banner, which only applies to that guarantor.
  guarantorSlot?: React.ReactNode;
  // A hint chip shown in that same guarantor's row header (e.g. "Verificación
  // disponible"); clicking the row expands to reveal the offer.
  guarantorChip?: React.ReactNode;
  // slug -> { valid, total }, for the "X de Y documentos validados" footer.
  docCounts?: Record<string, { valid: number; total: number }>;
  // The property ("Inmueble") rendered as the first part of the lease.
  inmuebleSlot?: React.ReactNode;
  // Whether the property is verified — counts toward the "partes validadas" summary.
  inmuebleValidated?: boolean;
}

type DocCounts = Record<string, { valid: number; total: number }>;

const docCountFor = (
  slugs: string[],
  counts?: DocCounts,
): { valid: number; total: number } | null => {
  if (!counts) return null;
  for (const s of slugs) if (counts[s]) return counts[s];
  return null;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

dayjs.extend(customParseFormat);

// Normalize a birth date to DD/MM/YYYY regardless of the incoming format: principals
// arrive pre-formatted as DD/MM/YYYY; additionals arrive raw from form_values (ISO).
const formatDate = (value?: string | null): string | null => {
  if (!value) return null;
  const parsed = dayjs(
    value,
    ["DD/MM/YYYY", "YYYY-MM-DD", "YYYY-MM-DDTHH:mm:ss"],
    false,
  );
  return parsed.isValid() ? parsed.format("DD/MM/YYYY") : value;
};

const initialsOf = (name: string): string => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return (parts[0][0] + (parts[1]?.[0] ?? "")).toUpperCase();
};

const isMoralType = (legalType: unknown): boolean =>
  legalType === 2 || legalType === "Moral" || legalType === "Legal" || legalType === "moral";

const AVATAR_STYLES: Record<Person["role"], string> = {
  tenant: "bg-emerald-50 text-emerald-700",
  guarantor: "bg-orange-50 text-orange-700",
  landlord: "bg-blue-50 text-blue-700",
  rep: "bg-violet-50 text-violet-700",
};

// ---------------------------------------------------------------------------
// Normalization (principal FK data + additional multiexpediente subfolders)
// ---------------------------------------------------------------------------

// Doc-group slugs for a PRINCIPAL participant (guarantors may live under the
// investigation slug, so we return both candidates and pick whichever exists).
const principalDocSlugs = (role: Person["role"], company: boolean): string[] => {
  const s = company ? "pm" : "pf";
  switch (role) {
    case "tenant":
      return [`tenant-${s}`];
    case "guarantor":
      return [`guarantor-${s}`, `guarantor-investigation-${s}`];
    case "landlord":
      return [`lease-contract-${s}`];
    default:
      return [];
  }
};

const principalToPerson = (
  data: TenantDetails | GuarantorDetails | LandlordDetails,
  role: Person["role"],
  roleLabel: string,
): Person => {
  const fullName = [data.name, data.first_surname, data.second_surname]
    .filter(Boolean)
    .join(" ")
    .trim();
  const company = isMoralType(data.legal_type);
  // Persona moral: show the company's razón social (legal_entity_name) as the header title
  // instead of the legal rep's composed name. Falls back to the name if it's missing. This
  // covers tenant + landlord principals; guarantors keep their own razón-social override below.
  const razonSocial = company ? data.legal_entity_name?.trim() : undefined;
  const displayName = razonSocial || fullName || data.name || "Sin nombre";
  // Persona moral: surface the legal rep so the nested "Representante legal" block shows a name.
  // The principal FK only carries the rep's name (legal_representative on TenantPerson);
  // guarantors enrich it (email/phone/rfc) from the multiexpediente principal in the override below.
  const legalRepName = company
    ? (data as { legal_representative?: string | null }).legal_representative?.trim()
    : undefined;
  return {
    id: `principal-${role}`,
    initials: initialsOf(displayName),
    fullName: displayName,
    roleLabel,
    legalTypeLabel: company ? "Persona moral" : "Persona física",
    isPrincipal: true,
    isCompany: company,
    isValidated: !!data.verified,
    detail: {
      email: data.email,
      phone: data.phone_number != null ? String(data.phone_number) : null,
      birthDate: formatDate(data.birth_date),
      rfc: data.rfc ?? null,
      curp: data.curp ?? null,
    },
    reps: [],
    legalRep: legalRepName ? { full_name: legalRepName } : null,
    role,
    defaultOpen: false,
    docSlugs: principalDocSlugs(role, company),
  };
};

const additionalToPerson = (
  entry: AdditionalEntry,
  role: Person["role"],
  roleLabel: string,
): Person => {
  const name =
    entry.full_name?.trim() ||
    (entry.person_index ? `Participante ${entry.person_index}` : "Participante");
  const company = isMoralType(entry.legal_type);
  return {
    id: entry.subfolder_id,
    initials: initialsOf(name),
    fullName: name,
    roleLabel,
    legalTypeLabel: company ? "Persona moral" : "Persona física",
    isPrincipal: entry.is_principal ?? false,
    isCompany: company,
    isValidated: entry.status === "valid",
    detail: {
      email: entry.email,
      phone: entry.phone,
      birthDate: formatDate(entry.birth_date),
      rfc: entry.rfc,
      curp: entry.curp,
    },
    reps: [],
    legalRep: entry.legal_rep ?? null,
    role,
    defaultOpen: false,
    docSlugs: entry.slug ? [entry.slug] : [],
  };
};

const buildGroups = ({
  tenant,
  guarantor,
  landlord,
  additionalParticipants,
}: Props): Group[] => {
  const ap = additionalParticipants ?? {};

  // Tenants
  const tenants: Person[] = [];
  if (tenant) tenants.push(principalToPerson(tenant, "tenant", "Inquilino"));
  (ap.tenants ?? []).forEach((e) =>
    tenants.push(additionalToPerson(e, "tenant", "Inquilino")),
  );

  // Guarantors. The principal comes from the FK when present; otherwise fall back to the
  // principal guarantor subfolder (is_principal) surfaced by the backend, so the OS still
  // shows. Avoid duplicating the principal when the FK already provides it.
  const guarantors: Person[] = [];
  const apGuarantors = ap.guarantors ?? [];
  if (guarantor) {
    const principal = principalToPerson(guarantor, "guarantor", "Obligado solidario");
    // Persona moral: enrich the PM principal from the multiexpediente principal subfolder — the
    // razón social as the title, and the legal rep (name + email/phone/rfc) for the nested
    // "Representante legal" block (the FK principal only carries the rep's name, if any).
    if (principal.isCompany) {
      const apPrincipal = apGuarantors.find((e) => e.is_principal);
      if (apPrincipal?.full_name) {
        principal.fullName = apPrincipal.full_name;
        principal.initials = initialsOf(apPrincipal.full_name);
      }
      if (apPrincipal?.legal_rep) {
        principal.legalRep = apPrincipal.legal_rep;
      }
    }
    guarantors.push(principal);
    apGuarantors
      .filter((e) => !e.is_principal)
      .forEach((e) =>
        guarantors.push(additionalToPerson(e, "guarantor", "Obligado solidario")),
      );
  } else {
    apGuarantors.forEach((e) =>
      guarantors.push(additionalToPerson(e, "guarantor", "Obligado solidario")),
    );
  }

  // Landlords + nested representatives
  const additionalLandlords = (ap.landlords ?? []).map((e) =>
    additionalToPerson(e, "landlord", "Propietario (copropietario)"),
  );
  const additionalLandlordIds = new Set(additionalLandlords.map((l) => l.id));

  const reps = (ap.representatives ?? []).map((e) => {
    const person = additionalToPerson(e, "rep", "Representante del propietario");
    return { entry: e, person };
  });

  // Attach reps to their additional-landlord owner; unmatched reps fall to the principal.
  additionalLandlords.forEach((l) => {
    l.reps = reps
      .filter((r) => r.entry.represents_subfolder_id === l.id)
      .map((r) => r.person);
  });
  const repsForPrincipal = reps
    .filter(
      (r) =>
        !r.entry.represents_subfolder_id ||
        !additionalLandlordIds.has(r.entry.represents_subfolder_id),
    )
    .map((r) => r.person);

  const landlords: Person[] = [];
  if (landlord) {
    const principalLandlord = principalToPerson(landlord, "landlord", "Propietario");
    principalLandlord.reps = repsForPrincipal;
    landlords.push(principalLandlord);
  }
  landlords.push(...additionalLandlords);

  const groups: Group[] = [];
  if (tenants.length)
    groups.push({ key: "tenants", title: "Inquilinos", people: tenants });
  if (guarantors.length)
    groups.push({ key: "guarantors", title: "Obligados solidarios", people: guarantors });
  if (landlords.length)
    groups.push({ key: "landlords", title: "Propietarios", people: landlords });

  // Open the first person of the first group by default (like the mockup).
  if (groups[0]?.people[0]) groups[0].people[0].defaultOpen = true;

  return groups;
};

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

const Avatar: React.FC<{ person: Person; size?: "md" | "sm" }> = ({
  person,
  size = "md",
}) => {
  const dim = size === "sm" ? "size-7 text-[10px]" : "size-9 text-[13px]";
  const shape = person.isCompany ? "rounded-lg" : "rounded-full";
  return (
    <div
      className={`flex ${dim} shrink-0 items-center justify-center ${shape} font-bold ${AVATAR_STYLES[person.role]}`}
    >
      {person.isCompany ? <Building2 className="size-[18px]" /> : person.initials}
    </div>
  );
};

const Field: React.FC<{ label: string; value?: string | null; span?: boolean }> = ({
  label,
  value,
  span,
}) => {
  const isEmpty = value == null || value === "";
  return (
    <div className={span ? "sm:col-span-2" : ""}>
      <div className="mb-0.5 text-xs text-muted-foreground">{label}</div>
      <div className="break-all text-sm font-medium">
        {isEmpty ? "Sin datos" : value}
      </div>
    </div>
  );
};

const PersonStatus: React.FC<{ validated: boolean }> = ({ validated }) =>
  validated ? (
    <StatusBadge status="success" label="Validado" />
  ) : (
    <StatusBadge status="warning" label="Pendiente" />
  );

// Smooth-scroll to a person's document group (in "Documentos del Expediente"), opening it
// if collapsed and briefly highlighting it. Both live on the same page (same DOM).
const scrollToDocsFor = (docSlugs: string[]) => {
  for (const slug of docSlugs) {
    const el = document.getElementById(`docs-${slug}`);
    if (!el) continue;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    const trigger = el.querySelector<HTMLButtonElement>("button[data-state]");
    if (trigger?.getAttribute("data-state") === "closed") trigger.click();
    el.classList.add("ring-2", "ring-primary", "ring-offset-2");
    window.setTimeout(
      () => el.classList.remove("ring-2", "ring-primary", "ring-offset-2"),
      1800,
    );
    return;
  }
};

const DocsButton: React.FC<{ slugs: string[]; label?: string }> = ({
  slugs,
  label = "Ver documentos",
}) =>
  slugs.length ? (
    <button
      type="button"
      onClick={() => scrollToDocsFor(slugs)}
      className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-2.5 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted/60"
    >
      <FileText className="size-3.5 text-muted-foreground" />
      {label}
    </button>
  ) : null;

const RepBlock: React.FC<{ rep: Person; docCounts?: DocCounts }> = ({
  rep,
  docCounts,
}) => {
  const count = docCountFor(rep.docSlugs, docCounts);
  return (
    <div className="mt-3 border-l-2 border-violet-200 pl-3.5">
      <div className="flex items-center gap-2.5">
        <Avatar person={rep} size="sm" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">{rep.fullName}</div>
          <div className="text-xs text-muted-foreground">{rep.roleLabel}</div>
        </div>
        <PersonStatus validated={rep.isValidated} />
      </div>
      <div className="mt-2 grid grid-cols-1 gap-2 pl-9 sm:grid-cols-3 sm:gap-x-5">
        <Field label="Correo" value={rep.detail.email} />
        <Field label="Teléfono" value={rep.detail.phone} />
        <Field label="RFC" value={rep.detail.rfc} />
      </div>
      {rep.docSlugs.length > 0 && (
        <div className="ml-9 mt-2.5 flex flex-wrap items-center gap-3 border-t border-dashed border-violet-200 pt-2.5">
          {count && (
            <span
              className={`inline-flex items-center gap-1.5 text-xs font-medium ${
                count.valid === count.total
                  ? "text-emerald-700"
                  : "text-muted-foreground"
              }`}
            >
              {count.valid === count.total ? (
                <Check className="size-3.5" strokeWidth={2.5} />
              ) : (
                <FileText className="size-3.5" />
              )}
              {count.valid} de {count.total} del representante
            </span>
          )}
          <div className="ml-auto">
            <DocsButton slugs={rep.docSlugs} />
          </div>
        </div>
      )}
    </div>
  );
};

const LegalRepBlock: React.FC<{ rep: LegalRep }> = ({ rep }) => (
  <div className="mt-3 border-l-2 border-violet-200 pl-3.5">
    <div className="flex items-center gap-2.5">
      <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-violet-50 text-[10px] font-bold text-violet-700">
        {initialsOf(rep.full_name || "RL")}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold">
          {rep.full_name || "Representante legal"}
        </div>
        <div className="text-xs text-muted-foreground">Representante legal</div>
      </div>
    </div>
    <div className="mt-2 grid grid-cols-1 gap-2 pl-9 sm:grid-cols-3 sm:gap-x-5">
      <Field label="Correo" value={rep.email} />
      <Field label="Teléfono" value={rep.phone} />
      <Field label="RFC" value={rep.rfc} />
    </div>
  </div>
);

const PersonRow: React.FC<{
  person: Person;
  slot?: React.ReactNode;
  chip?: React.ReactNode;
  docCounts?: DocCounts;
}> = ({ person, slot, chip, docCounts }) => {
  const subtitle =
    `${person.roleLabel} · ${person.legalTypeLabel}` +
    (person.reps.length
      ? ` · ${person.reps.length} representante${person.reps.length > 1 ? "s" : ""}`
      : "");
  const docCount = docCountFor(person.docSlugs, docCounts);

  return (
    <details
      className="group overflow-hidden rounded-xl border border-border"
      open={person.defaultOpen}
    >
      <summary className="flex cursor-pointer list-none items-start gap-3 px-3.5 py-2.5 hover:bg-muted/40 [&::-webkit-details-marker]:hidden">
        <Avatar person={person} />
        <div className="min-w-0 flex-1">
          {/* Name on its own line so it gets the full width (no 6-line wrap on mobile) */}
          <div className="text-sm font-semibold">{person.fullName}</div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
            <span>{subtitle}</span>
            {person.role !== "landlord" &&
              (person.isPrincipal ? (
                <span className="rounded border border-teal-200 bg-teal-50 px-1.5 text-[10px] font-bold uppercase text-teal-700">
                  Investigado
                </span>
              ) : (
                <span className="rounded border border-slate-200 bg-slate-100 px-1.5 text-[10px] font-bold uppercase text-slate-500">
                  No investigado
                </span>
              ))}
            {/* On mobile the status chips live under the name so it keeps full width */}
            <span className="flex items-center gap-2 sm:hidden">
              {chip}
              <PersonStatus validated={person.isValidated} />
            </span>
          </div>
        </div>
        {/* On desktop the status sits to the right of the name */}
        <div className="hidden shrink-0 items-center gap-2 sm:flex">
          {chip}
          <PersonStatus validated={person.isValidated} />
        </div>
        <ChevronDown className="mt-0.5 size-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" />
      </summary>
      <div className="border-t border-border px-3.5 pb-3.5 pt-3 sm:pl-[62px]">
        {person.isCompany ? (
          <>
            <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-3 sm:gap-x-5">
              <Field label="RFC de la empresa" value={person.detail.rfc} />
            </div>
            <LegalRepBlock rep={person.legalRep ?? {}} />
          </>
        ) : (
          <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-3 sm:gap-x-5">
            <Field label="Correo" value={person.detail.email} />
            <Field label="Teléfono" value={person.detail.phone} />
            <Field
              label="Fecha de nacimiento"
              value={person.detail.birthDate}
            />
            <Field label="RFC" value={person.detail.rfc} />
            <Field label="CURP" value={person.detail.curp} span />
          </div>
        )}
        {person.reps.map((rep) => (
          <RepBlock key={rep.id} rep={rep} docCounts={docCounts} />
        ))}
        {slot}
      </div>
      {/* 11a: footer action bar — consistent for PF / PM (reps use an inline link). */}
      {person.docSlugs.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 border-t border-border bg-muted/30 px-3.5 py-2.5">
          {docCount && (
            <span
              className={`inline-flex items-center gap-1.5 text-xs font-medium ${
                docCount.valid === docCount.total
                  ? "text-emerald-700"
                  : "text-muted-foreground"
              }`}
            >
              {docCount.valid === docCount.total ? (
                <Check className="size-3.5" strokeWidth={2.5} />
              ) : (
                <FileText className="size-3.5" />
              )}
              {docCount.valid} de {docCount.total}{" "}
              {person.isCompany
                ? "documentos de la empresa"
                : `documento${docCount.total !== 1 ? "s" : ""} validado${
                    docCount.valid !== 1 ? "s" : ""
                  }`}
            </span>
          )}
          <div className="ml-auto">
            <DocsButton
              slugs={person.docSlugs}
              label={
                person.isCompany
                  ? "Ver documentos de la empresa"
                  : "Ver documentos"
              }
            />
          </div>
        </div>
      )}
    </details>
  );
};

const PeopleReportSection: React.FC<Props> = (props) => {
  const groups = buildGroups(props);

  const allPeople = groups.flatMap((g) => g.people);
  const hasInmueble = !!props.inmuebleSlot;
  const total = allPeople.length + (hasInmueble ? 1 : 0);
  const validated =
    allPeople.filter((p) => p.isValidated).length +
    (hasInmueble && props.inmuebleValidated ? 1 : 0);
  const pending = total - validated;

  if (!total) return null;

  return (
    <Card className="gap-3 py-4">
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <Users className="size-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold">Partes del Arrendamiento</h3>
          <span className="ml-auto text-xs text-muted-foreground">
            {total} parte{total !== 1 ? "s" : ""} ·{" "}
            <span className="font-semibold text-emerald-700">{validated} validada{validated !== 1 ? "s" : ""}</span> ·{" "}
            <span className="font-semibold text-amber-700">{pending} pendiente{pending !== 1 ? "s" : ""}</span>
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {props.inmuebleSlot && (
          <div>
            <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
              Inmueble
            </div>
            <div className="rounded-xl border border-border px-3.5 py-3">
              {props.inmuebleSlot}
            </div>
          </div>
        )}
        {groups.map((group) => (
          <div key={group.key}>
            <div className="mb-2 text-[11px] font-bold uppercase tracking-wide text-muted-foreground">
              {group.title} · {group.people.length}
            </div>
            <div className="flex flex-col gap-2">
              {group.people.map((person) => (
                <PersonRow
                  key={person.id}
                  person={person}
                  docCounts={props.docCounts}
                  slot={
                    person.role === "guarantor" && person.isPrincipal
                      ? props.guarantorSlot
                      : undefined
                  }
                  chip={
                    person.role === "guarantor" && person.isPrincipal
                      ? props.guarantorChip
                      : undefined
                  }
                />
              ))}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default PeopleReportSection;
